<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import type { OrderListItemDto } from "@/api/orders/dto/order.dto";
import { API } from "@/api/api";
import type { TripOrder, TripStop } from "@/types/trip";
import TripManager from "@/views/trips/edit/services/tripManager/tripManager";
import { useRoute } from "vue-router";

const api = new API();

const route = useRoute();

const tripManager = new TripManager(api, route);

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    orders: TripOrder[];
    tripStops: TripStop[];
  }>(),
  {
    modelValue: false,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "change", value: TripOrder): void;
}>();

const orderId = ref<string | null>(null);
const orderNumber = ref<number | null>(null);
const orderItems = ref<OrderListItemDto[]>([]);
const search = ref("");
const isFetching = ref(false);
const isAttaching = ref(false);

const addOrderDialog = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

watch(search, (value) => {
  if (isFetching.value) return;
  fetchOrders(value);
});

async function attach() {
  if (!orderId.value) {
    return;
  }

  isAttaching.value = true;

  const tripOrder = await tripManager.loadTripOrder(orderId.value);

  if (!tripOrder) {
    return;
  }

  emit("change", {
    ...tripOrder,
  });

  isAttaching.value = false;
}

async function fetchOrders(term: string) {
  const params = { number: term, pageSize: 10 };

  isFetching.value = true;
  const response = await api!.orders.findOrders(params);
  if (response.success && response.data) {
    const tripOrderIds = props.orders.map((order) => order.id);

    const withoutTripNewOrders = response.data.items.filter(
      (order) => !order.tripId && !tripOrderIds.includes(order.id),
    );

    orderItems.value = withoutTripNewOrders;
  } else {
    console.log("Error loading orders", response.error);
  }
  isFetching.value = false;
}

function onUpdate(id: any) {
  orderId.value = id;
  orderNumber.value = orderItems.value.find((order) => order.id === id)?.number || null;
}
</script>

<template>
  <omni-dialog
    v-model="addOrderDialog"
    :primary-button-disabled="!orderId"
    :primary-button-loading="isAttaching"
    primary-action-label="Add"
    title="Add order"
    width="360"
    @primary-action:click="attach"
  >
    <div class="py-4">
      <v-autocomplete
        v-model:search="search"
        :items="orderItems"
        :loading="isFetching"
        :model-value="orderNumber"
        density="compact"
        hide-details
        item-title="number"
        item-value="id"
        label="Order number"
        variant="outlined"
        @update:model-value="onUpdate"
      />
    </div>
  </omni-dialog>
</template>
